export interface AnalyticsPage {
  path: string;
  title: string;
}

interface AnalyticsPageAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
}

const redirectOnlyPaths = new Set(['/pricing', '/reports/releases']);
const protectedPaths = new Set(['/dashboard', '/cutting-board', '/projects', '/assets', '/profile', '/social']);
const normalizeAnalyticsPath = (pathname: string): string =>
  pathname === '/' ? pathname : pathname.replace(/\/+$/, '') || '/';

export const shouldTrackAnalyticsPage = (
  pathname: string,
  authState: AnalyticsPageAuthState,
): boolean => {
  const normalizedPath = normalizeAnalyticsPath(pathname);
  if (authState.isLoading || redirectOnlyPaths.has(normalizedPath)) return false;
  if (normalizedPath === '/' && authState.isAuthenticated) return false;

  const isProtectedPath = protectedPaths.has(normalizedPath) || /^\/project\/[^/]+$/.test(normalizedPath);
  if (isProtectedPath && !authState.isAuthenticated) return false;

  return true;
};

export const describeAnalyticsPage = (pathname: string): AnalyticsPage => {
  const normalizedPath = normalizeAnalyticsPath(pathname);

  if (/^\/project\/[^/]+$/.test(normalizedPath)) {
    return { path: '/project/:projectId', title: 'Project editor' };
  }

  if (/^\/blog\/[^/]+$/.test(normalizedPath)) {
    return { path: '/blog/:slug', title: 'Blog post' };
  }

  const knownPages: Record<string, string> = {
    '/': 'Home',
    '/blog': 'Blog',
    '/help': 'Help',
    '/contact': 'Contact',
    '/terms': 'Terms',
    '/privacy': 'Privacy',
    '/api': 'API documentation',
    '/dashboard': 'Dashboard',
    '/cutting-board': 'Cutting board',
    '/projects': 'Projects',
    '/assets': 'Assets',
    '/profile': 'Profile',
    '/social': 'Social',
  };

  const title = knownPages[normalizedPath];
  if (title) {
    return { path: normalizedPath, title };
  }

  return { path: '/other', title: 'Other' };
};
